import {APP_ICONS, FallbackIcon} from "~/components/_appIcons";
import type {AppItem, AppCategory} from "~/components/_appsData";
import {appCategories} from "~/components/_appsData";
import {HighlightFirstLetters} from "~/components/_highlightFirstLetters";

/* ===========================================================================
 * AppsHero, direct port of growthjockey--website/app/components/sections/heroSection.tsx
 * Right-aligned text overlaid on a full-viewport video background. White text
 * on dark scene. Eyebrow → display title → body → CtaButtonEmpty, each
 * staggered with fade-in.
 * =========================================================================== */
function AppsHero() {
    return (
        <section
            aria-labelledby="apps-hero-title"
            className="relative w-full min-h-[75svh] overflow-hidden grid justify-items-end content-center pt-[var(--gj-header-height)] pb-10 lg:pb-16"
        >
            <video
                playsInline
                autoPlay
                muted
                loop
                preload="auto"
                aria-label="Stacked blue cubes forming a structured tower symbolizing every app in one operating system."
                className="absolute inset-0 w-full h-full object-cover"
                poster="https://growthjockey-prod.b-cdn.net/new/heroPoster02.webp"
            >
                <source
                    src="https://growthjockey-prod.b-cdn.net/new/heroBanner02.mp4"
                    type="video/mp4"
                    media="(min-width: 640px)"
                />
                <source
                    src="https://growthjockey-prod.b-cdn.net/new/heroBannerMobile2.webm"
                    type="video/webm"
                />
            </video>

            <div className="relative grid grid-cols-1 lg:grid-cols-[1fr_5fr] xl:grid-cols-[1fr_2fr] h-fit gj-px-screen-edge-constrained pt-32 lg:pt-0">
                <div className="max-lg:hidden" />
                <div className="grid grid-cols-1 grid-flow-row gap-4 text-[#FFFFFF] text-right justify-items-end">
                    <h1
                        id="apps-hero-title"
                        className="text-heading0 opacity-0 animate-fade-in"
                        style={{animationDelay: "200ms"}}
                    >
                        Every app to run your venture.
                    </h1>
                    <div
                        aria-hidden="true"
                        className="h-1 w-32 cta-bg-gradient rounded-full opacity-0 animate-fade-in"
                        style={{animationDelay: "300ms"}}
                    />
                    <p
                        className="text-body opacity-0 animate-fade-in lg:max-w-[45rem]"
                        style={{animationDelay: "400ms"}}
                    >
                        24 apps · 5 categories · one OS. Software for marketing, sales, operations, people, and intelligence, each app stands on its own, all of
                        them share a single data layer.
                    </p>
                </div>
            </div>
        </section>
    );
}

/* ===========================================================================
 * App cards, Odoo-style structure (icon-in-tile + name + description) but
 * rebuilt with GJ tokens. Outer pill in surface-secondary-background;
 * inner icon on surface-surface-card (white) with a soft elevation shadow;
 * primary-500 mono icon. Hover lifts the card and warms the icon shadow.
 * =========================================================================== */

/* Card-background rotation, three "rendered tile" gradient meshes cycled by
 * position. Defined in tailwind.css. Each is a distinct lit/glossy treatment
 * over a different blue base, mimicking GJ's industry-tile rhythm without
 * paying the image-asset cost. Swap to bg-[url(...)] classes here if/when we
 * have real textured artwork. */
const CARD_BACKGROUNDS = ["card-bg-1", "card-bg-2", "card-bg-3"] as const;

function AppCard({app, index}: {app: AppItem; index: number}) {
    const Icon = APP_ICONS[app.slug] ?? FallbackIcon;
    const bg = CARD_BACKGROUNDS[index % CARD_BACKGROUNDS.length];
    return (
        <div className={`grid grid-cols-[auto_minmax(0,1fr)] gap-md2 lg:gap-md3 items-center p-md3 rounded-2xl ${bg} overflow-hidden`}>
            <div className="w-16 h-16 lg:w-[4.5rem] lg:h-[4.5rem] rounded-xl bg-surface-surface-card grid place-items-center shrink-0">
                <Icon
                    size={28}
                    className="text-primary-500"
                />
            </div>
            <div className="min-w-0 grid gap-1">
                <h3 className="text-main-title font-semibold text-[#FFFFFF]">{app.name}</h3>
                <p className="text-nav-text text-[#FFFFFF]/70">{app.description}</p>
            </div>
        </div>
    );
}

function CategorySection({category}: {category: AppCategory}) {
    return (
        <section
            id={category.id}
            aria-labelledby={`${category.id}-title`}
            className="scroll-mt-24 grid gap-md3 lg:gap-lg"
        >
            <div className="grid gap-md justify-items-start">
                <h2
                    id={`${category.id}-title`}
                    className="text-heading2-25 text-text-primary"
                >
                    <HighlightFirstLetters>{category.name}</HighlightFirstLetters>
                </h2>
                <div
                    aria-hidden="true"
                    className="h-1 w-20 cta-bg-gradient rounded-full"
                />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-md2 lg:gap-md3">
                {category.apps.map((app, idx) => (
                    <AppCard
                        key={app.slug}
                        app={app}
                        index={idx}
                    />
                ))}
            </div>
        </section>
    );
}

/* Editorial TOC, each category gets a 4px top rail in its own gradient that
 * paints across on hover, with the section number + name stacked below. The
 * per-category gradient (currently unused elsewhere on the page) becomes the
 * single point of color identity. */
function CategoryNav() {
    return (
        <nav
            aria-label="Jump to category"
            className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5"
        >
            {appCategories.map((cat, idx) => (
                <a
                    key={cat.id}
                    href={`#${cat.id}`}
                    className="group grid no-underline"
                >
                    <div className="h-1 bg-surface-lines-and-borders relative overflow-hidden">
                        <div
                            aria-hidden="true"
                            className="absolute inset-y-0 left-0 w-8 cta-bg-gradient transition-[width] duration-500 ease-out group-hover:w-full"
                        />
                    </div>
                    <div className="grid gap-1 pt-md3 pb-md3 px-md transition-colors duration-300 group-hover:bg-surface-secondary-background">
                        <span className="text-nav-text text-text-tertiary tabular-nums">{String(idx + 1).padStart(2, "0")}</span>
                        <span className="text-medium-b text-text-primary group-hover:text-primary-500 transition-colors duration-300">{cat.name}</span>
                    </div>
                </a>
            ))}
        </nav>
    );
}

export default function AllAppsRoute() {
    return (
        <div className="w-full">
            <AppsHero />

            <div className="max-w-[1120px] mx-auto px-5 pt-16 lg:pt-24 pb-24 lg:pb-32 grid gap-16 lg:gap-24">
                <CategoryNav />
                {appCategories.map((cat) => (
                    <CategorySection
                        key={cat.id}
                        category={cat}
                    />
                ))}
            </div>
        </div>
    );
}
